import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  PaperClipOutlined,
  CloseCircleFilled,
  SmileOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import Picker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import {
  useAuthStore,
  useChannelStore,
  useChannelMemberStore,
  useDmStore,
  useMessageStore,
  useWsStore,
} from '../stores';
import SlashCommandPicker from './SlashCommandPicker';
import Toolbar from './Toolbar';
import { useSlashCommands } from '../hooks/useSlashCommands';
import { useComposerDraft } from '../hooks/useComposerDraft';
import { useRecentInputHistory } from '../hooks/useRecentInputHistory';
import type { RecentInputEntry } from '../hooks/useRecentInputHistory';
import { useDismissibleLayer } from '../hooks/useDismiss';
import styles from './MessageInput.module.css';
import { createMentionExtension } from '../extensions/mention';
import { commandRegistry, CommandError } from '../commands/registry';
import type { CommandDefinition, RemoteCommand, CommandContext } from '../commands/registry';
import { isRemoteCommand } from './SlashCommandPicker';
import '../commands/builtins';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import { toUploadFile } from '../hosts/web/files';
import { onMessageAcked, dispatchOutgoingMessage } from '../stores/actions';
import { randomUUID } from '../lib/uuid';
import type { ChannelMember } from '../lib/api';
import type { SendStatus } from '../types';

interface Props {
  channelId: string;
  /** Names the draft this composer persists its unsent text under; see `useComposerDraft`. */
  draftKey: string;
  /**
   * Takes over sending. It must reject when the message neither reached the
   * wire nor became a pending row the member can retry from, so the composer
   * can keep the text and its draft instead of clearing them.
   */
  onSendOverride?: (content: string, mentions: string[]) => Promise<void>;
  /** A contextual starter may populate this composer once; user edits win afterwards. */
  suggestedText?: string;
  onSuggestedTextApplied?: () => void;
  /** Read-only conversations retain history but must not accept outbound input. */
  disabled?: boolean;
}

function resolveRecentInputScopeKey(
  draftKey: string,
  channelId: string,
  channelType: 'dm' | 'channel',
): string {
  if (draftKey.startsWith('thread:')) return draftKey;
  if (draftKey.startsWith('pending-thread:')) return draftKey;
  if (draftKey.startsWith('channel:')) {
    return `${channelType}:${channelId}`;
  }
  return draftKey;
}

function getMarkdownFromEditor(ed: { storage: unknown; getText: () => string }): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mdStorage = (ed.storage as any).markdown as { getMarkdown(): string } | undefined;
  return mdStorage?.getMarkdown() ?? ed.getText();
}

function extractMentionIds(ed: {
  getJSON: () => { content?: Array<Record<string, unknown>> };
}): string[] {
  const ids: string[] = [];
  const walk = (node: Record<string, unknown>) => {
    if (
      node.type === 'mention' &&
      typeof (node.attrs as Record<string, unknown>)?.id === 'string'
    ) {
      ids.push((node.attrs as Record<string, string>).id);
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) walk(child as Record<string, unknown>);
    }
  };
  const json = ed.getJSON();
  if (json.content) {
    for (const node of json.content) walk(node as Record<string, unknown>);
  }
  return [...new Set(ids)];
}

const EMPTY_MEMBERS: ChannelMember[] = [];

export default function MessageInput({
  channelId,
  draftKey,
  onSendOverride,
  suggestedText,
  onSuggestedTextApplied,
  disabled = false,
}: Props) {
  const currentUser = useAuthStore((s) => s.currentUser);
  const channels = useChannelStore((s) => s.channels);
  const dmChannels = useDmStore((s) => s.dmChannels);
  const channel = channels.find((c) => c.id === channelId);
  const dmChannel = dmChannels.find((c) => c.id === channelId);
  const channelType: 'dm' | 'channel' = dmChannel || channel?.type === 'dm' ? 'dm' : 'channel';
  const channelMembersVersion = useChannelStore((s) => s.channelMembersVersion[channelId] ?? 0);
  const sendWsMessage = useWsStore((s) => s.sendWsMessage);
  const addPendingMessage = useMessageStore((s) => s.addPendingMessage);
  const failPendingMessage = useMessageStore((s) => s.failPendingMessage);
  const [text, setText] = useState('');
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const [commandError, setCommandError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastTypingSent = useRef(0);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);
  const {
    restore: restoreDraft,
    record: recordDraft,
    clear: clearDraft,
  } = useComposerDraft(draftKey);
  const { recordSentInput, recallOlderInput, recallNewerInput, clearRecallState, hasActiveRecall } =
    useRecentInputHistory(resolveRecentInputScopeKey(draftKey, channelId, channelType));

  // Preview URLs for pending files (revoked on change).
  const previewUrls = useMemo(
    () => pendingFiles.map((f) => URL.createObjectURL(f)),
    [pendingFiles],
  );
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const [slashResolvedUser, setSlashResolvedUser] = useState<
    { id: string; username: string } | undefined
  >();

  // The picker answers what the member is typing now. Text hydrated from a
  // draft was written in an earlier sitting and does not open it; the next edit
  // does.
  const [editedSinceHydration, setEditedSinceHydration] = useState(false);

  const slash = useSlashCommands(text, editedSinceHydration);

  const mentionMembers = useChannelMemberStore((s) => s.byChannelId[channelId] ?? EMPTY_MEMBERS);
  const loadChannelMembers = useChannelMemberStore((s) => s.loadChannelMembers);

  const membersVersion = channelMembersVersion;

  useEffect(() => {
    void loadChannelMembers(channelId, membersVersion).catch(() => {});
  }, [channelId, membersVersion, loadChannelMembers]);

  const mentionUsersRef = useRef<ChannelMember[]>(mentionMembers);
  mentionUsersRef.current = mentionMembers;

  const mentionActiveRef = useRef(false);

  // Refs keep `createMentionExtension`'s identity stable for the suggestion
  // render closure while still tracking the channel the composer is currently
  // rendered in: a DM's mention popover has its own empty-state copy, since its
  // candidate set is capped at the two members and cannot be widened.
  const channelTypeRef = useRef<'dm' | 'channel'>(channelType);
  channelTypeRef.current = channelType;

  const mentionExtension = useMemo(
    () =>
      createMentionExtension(
        () => mentionUsersRef.current,
        mentionActiveRef,
        () => channelTypeRef.current,
      ),
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Markdown.configure({
        html: false,
        transformCopiedText: true,
        transformPastedText: true,
      }),
      mentionExtension,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
        'data-placeholder': '输入消息... (Enter 发送, Ctrl+Enter 换行)',
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files;
        if (files) {
          const imageFiles: File[] = [];
          for (let i = 0; i < files.length; i++) {
            const file = files[i]!;
            if (file.type.startsWith('image/')) {
              imageFiles.push(file);
            }
          }
          if (imageFiles.length > 0) {
            event.preventDefault();
            setPendingFiles((prev) => [...prev, ...imageFiles]);
            return true;
          }
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (items) {
          const imageFiles: File[] = [];
          for (let i = 0; i < items.length; i++) {
            const item = items[i]!;
            if (item.type.startsWith('image/')) {
              const file = item.getAsFile();
              if (file) imageFiles.push(file);
            }
          }
          if (imageFiles.length > 0) {
            event.preventDefault();
            setPendingFiles((prev) => [...prev, ...imageFiles]);
            return true;
          }
        }
        return false;
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          if (event.ctrlKey || event.metaKey) {
            return false;
          }
          if (mentionActiveRef.current) return false;
          event.preventDefault();
          handleSendRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const newText = getMarkdownFromEditor(ed);
      setText(newText);
      recordDraft(ed.isEmpty ? null : ed.getJSON());
      clearRecallState();
      setEditedSinceHydration(true);
      // A failure banner describes the send that was attempted, not the message
      // the member is now writing in its place.
      setSendStatus((status) => (status === 'error' ? 'idle' : status));
      emitTyping();

      if (slashResolvedUser) {
        setSlashResolvedUser(undefined);
      }
    },
  });

  // Names the surface whose text the box is showing. A send is issued against
  // one surface and may return against another, so it is this, and not the key
  // the send was started with, that says whether the box on screen is still the
  // one that send is entitled to touch.
  const shownDraftKeyRef = useRef(draftKey);

  // Fills the composer from the stored draft whenever it takes on a new key —
  // a channel switch, or a thread panel swapping surfaces. `restoreDraft`
  // changes identity only with the key, so this never fights live typing, and
  // an empty draft deliberately empties the box: the previous key's text must
  // not follow the member into the next one.
  useEffect(() => {
    // Read-only state is not a document edit. Emitting an update here can queue
    // an empty draft during StrictMode's mount replay before hydration runs.
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);

  useEffect(() => {
    if (!suggestedText || !editor || editor.isDestroyed) return;
    editor.commands.setContent(suggestedText, { emitUpdate: false });
    setText(suggestedText);
    setEditedSinceHydration(true);
    editor.commands.focus('end');
    onSuggestedTextApplied?.();
  }, [editor, suggestedText, onSuggestedTextApplied]);

  useEffect(() => {
    shownDraftKeyRef.current = draftKey;
    if (!editor) return;
    const doc = restoreDraft();
    editor.commands.setContent(doc ?? '', { emitUpdate: false });
    setText(doc === null ? '' : getMarkdownFromEditor(editor));
    clearRecallState();
    setEditedSinceHydration(false);
    // A failure reported under the composer describes the send that was
    // attempted on the outgoing surface; neither banner follows it to the next.
    setSendStatus('idle');
    setCommandError(null);
    // Replacing the document puts the caret back at the start, which is worth
    // correcting only for a member who is already typing here. A composer that
    // does not hold focus must not take it: this effect also runs on a cold
    // boot and on every surface the composer is swapped onto, and stealing
    // focus there yanks the caret out of whatever is actually in use and pops
    // the soft keyboard on mobile.
    if (editor.isFocused) editor.commands.focus('end');
  }, [editor, draftKey, restoreDraft, clearRecallState]);

  const handleSendRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!commandError) return;
    const timer = setTimeout(() => setCommandError(null), 4000);
    return () => clearTimeout(timer);
  }, [commandError]);

  const executeCommand = useCallback(
    async (name: string, args: string, resolvedUser?: { id: string; username: string }) => {
      const cmd = commandRegistry.get(name);
      if (!cmd) return false;
      const ctx: CommandContext = {
        channelId,
        currentUserId: currentUser?.id ?? '',
        args,
        resolvedUser,
        api,
      };
      await cmd.execute(ctx);
      return true;
    },
    [channelId, currentUser],
  );

  // A send that awaits a round trip outlives the surface it was issued on: a
  // channel switch swaps the box's contents, `/leave` and `/dm` navigate away,
  // and a confirmed thread creation replaces the panel. What such a send does
  // when it returns therefore belongs to the surface it started on, and only
  // reaches the box while that surface is still the one being shown.
  const isShowing = useCallback(
    (sentDraftKey: string) => shownDraftKeyRef.current === sentDraftKey,
    [],
  );

  // Empties the box and its draft together once the message is no longer the
  // composer's to hold. The draft dropped is the one the send was issued
  // under; the box is only emptied while it is still showing that surface,
  // because `clearContent` reports an empty document through `onUpdate`, which
  // would record over — and delete — the draft the composer has since adopted.
  const clearAfterSend = useCallback(
    (sentDraftKey: string) => {
      clearDraft(sentDraftKey);
      clearRecallState();
      if (!isShowing(sentDraftKey)) return;
      setText('');
      if (editor && !editor.isDestroyed) {
        editor.commands.clearContent();
        editor.commands.focus();
      }
    },
    [editor, clearDraft, isShowing, clearRecallState],
  );

  const applyRecentInput = useCallback(
    (nextInput: RecentInputEntry) => {
      if (!editor || editor.isDestroyed) return;
      if (nextInput.markdown.length === 0) {
        editor.commands.setContent('', { emitUpdate: false });
        setText('');
        recordDraft(null);
        editor.commands.focus();
        return;
      }
      editor.commands.setContent(nextInput.doc ?? nextInput.markdown, { emitUpdate: false });
      setText(nextInput.markdown);
      recordDraft(editor.getJSON());
      editor.commands.focus('end');
    },
    [editor, recordDraft],
  );

  const handleSend = useCallback(async () => {
    if (!editor) return;

    const markdown = getMarkdownFromEditor(editor);
    const content = markdown.trim();
    if (disabled || (!content && pendingFiles.length === 0) || sendStatus === 'sending') return;
    const recentInputDoc = markdown === content ? editor.getJSON() : null;

    setCommandError(null);

    if (content.startsWith('/')) {
      const spaceIdx = content.indexOf(' ');
      const name = spaceIdx === -1 ? content.slice(1) : content.slice(1, spaceIdx);
      const args = spaceIdx === -1 ? '' : content.slice(spaceIdx + 1).trim();
      const cmd = commandRegistry.get(name);
      if (cmd) {
        try {
          await executeCommand(name, args, slashResolvedUser);
          recordSentInput({ markdown: content, doc: recentInputDoc });
          setSlashResolvedUser(undefined);
          clearAfterSend(draftKey);
        } catch (err) {
          // A command that failed after the composer left the surface it was
          // typed on has no banner to appear under — that surface's draft still
          // holds the text, and the console is what is left to say why it did
          // not run.
          if (!isShowing(draftKey)) {
            console.error('composer command failed:', err);
            return;
          }
          if (err instanceof CommandError) {
            setCommandError(err.message);
          } else if (err instanceof ApiError) {
            setCommandError(err.message);
          } else {
            setCommandError(err instanceof Error ? err.message : 'Command failed');
          }
        }
        return;
      }
    }

    const mentionIds = extractMentionIds(editor);

    // Replace mention nodes in markdown with <@userId> format
    let finalContent = content;
    const json = editor.getJSON();
    const mentionMap = new Map<string, string>();
    const walkForLabels = (node: Record<string, unknown>) => {
      if (node.type === 'mention') {
        const attrs = node.attrs as Record<string, string>;
        if (attrs.id && attrs.label) {
          mentionMap.set(attrs.label, attrs.id);
        }
      }
      if (Array.isArray(node.content)) {
        for (const child of node.content) walkForLabels(child as Record<string, unknown>);
      }
    };
    if (json.content) {
      for (const node of json.content) walkForLabels(node as Record<string, unknown>);
    }
    for (const [label, id] of mentionMap) {
      finalContent = finalContent.replace(new RegExp(`@${escapeRegex(label)}`, 'g'), `<@${id}>`);
    }

    if (onSendOverride) {
      setSendStatus('sending');
      try {
        await onSendOverride(finalContent, mentionIds);
      } catch (err) {
        // Nothing went out, so the text stays in the box and its draft stays in
        // storage; the member retries from where they were.
        console.error('composer send override failed:', err);
        if (isShowing(draftKey)) setSendStatus('error');
        return;
      }
      recordSentInput({ markdown: content, doc: recentInputDoc });
      if (isShowing(draftKey)) setSendStatus('idle');
      clearAfterSend(draftKey);
      return;
    }

    // An attachment goes up over REST, which answers with the stored message,
    // so this rail reconciles its own optimistic row instead of waiting for an
    // ack frame.
    if (pendingFiles.length > 0) {
      const filesToSend = [...pendingFiles];
      setPendingFiles([]);
      const clientMessageId = randomUUID();
      addPendingMessage({
        clientMessageId,
        channelId,
        content: finalContent,
        contentType: 'text',
        status: 'pending',
        createdAt: Date.now(),
        senderName: currentUser?.display_name ?? 'Unknown',
        senderId: currentUser?.id ?? '',
        mentions: mentionIds,
      });
      recordSentInput({ markdown: content, doc: recentInputDoc });
      clearAfterSend(draftKey);
      try {
        const msg = await api.sendMessageWithAttachments(
          channelId,
          finalContent,
          await Promise.all(filesToSend.map(toUploadFile)),
        );
        onMessageAcked(channelId, clientMessageId, msg);
      } catch (err) {
        console.error('send with attachments failed:', err);
        failPendingMessage(clientMessageId, channelId);
      }
      return;
    }

    dispatchOutgoingMessage({
      channelId,
      content: finalContent,
      contentType: 'text',
      mentions: mentionIds,
    });
    recordSentInput({ markdown: content, doc: recentInputDoc });
    clearAfterSend(draftKey);
  }, [
    editor,
    draftKey,
    sendStatus,
    channelId,
    currentUser,
    executeCommand,
    slashResolvedUser,
    addPendingMessage,
    failPendingMessage,
    pendingFiles,
    onSendOverride,
    clearAfterSend,
    isShowing,
    recordSentInput,
  ]);

  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  const handleSlashSelect = useCallback(
    async (cmd: CommandDefinition | RemoteCommand) => {
      if (isRemoteCommand(cmd)) {
        const newText = `/${cmd.name} `;
        setText(newText);
        editor?.commands.setContent(newText);
        slash.close();
        editor?.commands.focus('end');
        return;
      }
      if (cmd.paramType === 'none') {
        const ctx: CommandContext = {
          channelId,
          currentUserId: currentUser?.id ?? '',
          args: '',
          api,
        };
        try {
          await cmd.execute(ctx);
        } catch (err) {
          if (!isShowing(draftKey)) {
            console.error('composer command failed:', err);
          } else if (err instanceof CommandError || err instanceof ApiError) {
            setCommandError(err.message);
          } else {
            setCommandError(err instanceof Error ? err.message : 'Command failed');
          }
        }
        clearAfterSend(draftKey);
        slash.close();
      } else {
        const newText = `/${cmd.name} `;
        setText(newText);
        editor?.commands.setContent(newText);
        slash.close();
        editor?.commands.focus('end');
      }
    },
    [channelId, draftKey, currentUser, slash, editor, clearAfterSend, isShowing],
  );

  const handleEditorKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (slash.isActive && slash.totalCount > 0) {
        if (slash.handleKeyDown(e)) return;
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const cmd = slash.selectedItem;
          if (cmd) handleSlashSelect(cmd);
          return;
        }
      }
      if (
        editor &&
        !mentionActiveRef.current &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
        (text.length === 0 || hasActiveRecall())
      ) {
        const nextInput = e.key === 'ArrowUp' ? recallOlderInput(text) : recallNewerInput();
        if (nextInput !== null) {
          e.preventDefault();
          applyRecentInput(nextInput);
          setEditedSinceHydration(false);
          return;
        }
      }
    },
    [
      slash,
      handleSlashSelect,
      editor,
      text,
      recallOlderInput,
      recallNewerInput,
      hasActiveRecall,
      applyRecentInput,
    ],
  );

  const emitTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSent.current < 2000) return;
    lastTypingSent.current = now;
    sendWsMessage({ type: 'typing', channel_id: channelId });
  }, [channelId, sendWsMessage]);

  const insertEmojiAtCursor = useCallback(
    (emoji: string) => {
      editor?.commands.insertContent(emoji);
    },
    [editor],
  );

  const closeEmojiPicker = useCallback(() => setEmojiPickerOpen(false), []);
  useDismissibleLayer({
    active: emojiPickerOpen,
    within: [emojiPickerRef, emojiBtnRef],
    onEscape: closeEmojiPicker,
    onOutsidePress: closeEmojiPicker,
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const files = e.target.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        setPendingFiles((prev) => [...prev, ...imageFiles]);
      }
    }
    e.target.value = '';
  };

  const statusText = () => {
    switch (sendStatus) {
      case 'sending':
        return '发送中...';
      case 'error':
        return '发送失败';
      default:
        return null;
    }
  };

  return (
    <div className={styles.root} data-message-input-root data-disabled={disabled || undefined}>
      <SlashCommandPicker
        groups={slash.filtered}
        visible={slash.isActive}
        selectedIndex={slash.selectedIndex}
        onSelect={handleSlashSelect}
        onClose={slash.close}
      />

      {/* Attachment preview strip */}
      {pendingFiles.length > 0 && (
        <div className={styles.attachmentPreviewStrip}>
          {pendingFiles.map((file, i) => (
            <div key={i} className={styles.attachmentPreviewItem}>
              <img src={previewUrls[i]} alt={file.name} className={styles.attachmentPreviewThumb} />
              <button
                className={styles.attachmentPreviewRemove}
                onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                title="移除"
              >
                <CloseCircleFilled />
              </button>
              <span className={styles.attachmentPreviewName}>{file.name}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.inputRow}>
        <button
          className={`icon-btn ${styles.uploadButton}`}
          onClick={() => fileInputRef.current?.click()}
          title="上传图片"
          disabled={disabled}
        >
          <PaperClipOutlined />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <div className={`tiptap-wrapper ${styles.tiptapWrapper}`} onKeyDown={handleEditorKeyDown}>
          <EditorContent editor={editor} />
        </div>
        <button
          className={`btn btn-primary ${styles.sendButton}`}
          data-testid="message-send"
          onClick={handleSend}
          disabled={
            disabled || (!text.trim() && pendingFiles.length === 0) || sendStatus === 'sending'
          }
        >
          <SendOutlined />
        </button>
      </div>

      {commandError && <div className={`${styles.status} ${styles.statusError}`} data-testid="message-send-status" data-error>{commandError}</div>}
      {statusText() && (
        <div className={`${styles.status} ${sendStatus === 'error' ? styles.statusError : ''}`} data-testid="message-send-status" data-error={sendStatus === 'error' || undefined}>
          {statusText()}
        </div>
      )}
    </div>
  );
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
